import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import { useTheme } from '@material-ui/core/styles';
import { useMediaQuery, Grid, Divider } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import debounce from 'lodash/debounce';
import SearchIcon from '@material-ui/icons/Search';
import { get } from 'lodash';
import { useRouter } from 'next/router';
import useStyles from './styles';
import { createEntity, deleteEntity, getList, updateOne } from '../../../../App/actions';
import { PRODUCTIONS, FILTER_AGGREGATION_SEARCH, PRODUCTION_PRIVACY } from '../../../../../constants/endpointConstants';
import {
  deleteProductionAction,
  getChosenProfileProductionsAction,
  getProfileWorksListAction,
  removeProductionAction,
  publishProductionAction,
  clearMVList,
  setStartingOfMVAggregation,
} from '../../../actions';
import FbPagination from '../../../../../components/FbPagination';
import {
  selectArtistProductionLoading,
  selectChosenProfileProductions,
  selectChosenProfileSeasons,
  selectChosenProfileWorks,
} from '../../../selectors';
import FbCircularProgress from '../../../../../components/FbCircularProgress';
import FormikAutocompleteSelect from '../../../../../components/Fields/FormikAutocompleteSelect';
import { Router } from '../../../../../src/i18n';
import {
  TP,
  performancesAggregationTypes,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  performancesAddedByIdentifiers,
} from '../../../../../constants/consts';
import Button from '../../../../../components/Button';
import EditArtistProductionCard from '../../components/EditArtistProductionCard';
import route, { artistsPaths } from '../../../../../constants/routes';
import { canDoProductionAction, getRouteWithSlug, checkDateForProductionDeletion } from '../../../../../utils/common';
import PublishProductionModal from '../../../../../components/EditComponents/PublishProductionModal';
import ConfirmDeleteModal from '../../../../../components/EditComponents/ConfirmDeleteModal';
import H3 from '../../../../../components/H3';
import Title from '../../../../../components/Title';
import { defaultArtistProductionsPagination } from '../../../constants';
import { PRODUCTIONS_TYPES } from '../../../../Company/constants';
import * as API from '../../../../../utils/API';
import ParseHtml from '../../../../../components/ParseHtml';
import Legend from '../../../../../components/Legend';
import usePermissions from '../../../../../utils/permissions';
import { selectUserConstants } from '../../../../../store/selectors';
import { canArtistEditProduction } from '../../../../../utils/editCommon';

const { profileAgg, seasonAgg, workAgg, typeAgg, queryAgg } = performancesAggregationTypes;
const { published, unpublished } = PRODUCTIONS_TYPES;

export function PerformancesTab({ t, tab, profile, privacyOptions }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const FilterFormRef = React.useRef(null);
  const urlQuery = useRouter().query;

  const artistProductions = useSelector(selectChosenProfileProductions());
  const seasonsOptions = useSelector(selectChosenProfileSeasons());
  const works = useSelector(selectChosenProfileWorks());
  const productionLoading = useSelector(selectArtistProductionLoading());
  const userConstants = useSelector(selectUserConstants());
  const { productionPermissions, isAdmin } = usePermissions(profile?.id);

  const staticOptions = [
    { value: published, label: t(`${TP}.m_ALLYEAR`) },
    { value: unpublished, label: t(`${TP}.FN_EDITSEASON_UNPUBLISHED_STATUS`) },
  ];
  const getSeasonsOptions = React.useCallback(
    () => [...staticOptions, ...seasonsOptions],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seasonsOptions],
  );
  const [mwValue, setMvValue] = React.useState(null);

  const [interactedProduction, setInteractedProduction] = React.useState(null);
  const [deletedProduction, setDeletedProduction] = React.useState(null);
  const [removedProduction, setRemovedProduction] = React.useState(null);
  const [productionsType, setProductionsType] = React.useState(
    staticOptions.find(option => option?.value === urlQuery['productions-type']) || staticOptions[0],
  );

  const getProductions = (pagination, queryOptions) => {
    dispatch(
      getChosenProfileProductionsAction({
        profileId: profile?.id,
        withPrivacy: queryOptions?.published !== false,
        params: {
          queryParams: {
            [profileAgg]: profile?.id,
            as_edit: true,
            ...queryOptions,
          },
          pagination,
        },
      }),
    );
  };

  const getPublishedProductionSeasonQueryValue = seasonValue => (seasonValue === published ? '' : seasonValue);

  const draftProductionsQueryOptions = {
    published: false,
    [workAgg]: mwValue?.value,
    added_by: performancesAddedByIdentifiers.ARTIST,
    for_entity_id: profile?.id,
    sort: '-updated_at',
  };

  const publishedProductionsQueryOptions = seasonQueryValue => ({
    [seasonAgg]: getPublishedProductionSeasonQueryValue(seasonQueryValue),
    [workAgg]: mwValue?.value,
    show_hidden: true,
    sort: '-year',
  });

  const seasonChange = v => {
    const newProductionType = v?.value === unpublished ? v : staticOptions[0];
    if (newProductionType.value !== productionsType.value) {
      setProductionsType(newProductionType);
      Router.push(
        artistsPaths.edit,
        `${getRouteWithSlug(route.ARTISTS, profile)}/edit?tab=schedule&productions-type=${newProductionType.value}`,
        { shallow: true },
      );
    }
    getProductions(
      defaultArtistProductionsPagination,
      v?.value === unpublished ? draftProductionsQueryOptions : publishedProductionsQueryOptions(v?.value),
    );
  };

  const paginationPageChange = pagination => {
    const values = FilterFormRef.current?.values;
    const seasonValue = values[seasonAgg]?.value;
    getProductions(
      pagination,
      seasonValue === unpublished ? draftProductionsQueryOptions : publishedProductionsQueryOptions(seasonValue),
    );
  };

  const goToAddNewProductionPage = () => {
    Router.push(artistsPaths.searchProductions, `${getRouteWithSlug(route.ARTISTS, profile)}/edit/search-productions`);
  };

  const setMusicalWorkValue = option => {
    // eslint-disable-next-line no-unused-expressions
    const values = FilterFormRef.current?.values;
    setMvValue(option);
    const query = { [workAgg]: option?.value || '' };
    if (values[seasonAgg]?.value === unpublished) {
      query.published = false;
      query.added_by = performancesAddedByIdentifiers.ARTIST;
    } else {
      query[seasonAgg] = getPublishedProductionSeasonQueryValue(values[seasonAgg]?.value);
      query.show_hidden = true;
    }
    getProductions(defaultArtistProductionsPagination, query);
  };

  const clearMusicalWorksList = () => {
    dispatch(clearMVList());
  };

  const getWorksList = debounce(value => {
    dispatch(
      getList({
        endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
        sagaRoutine: getProfileWorksListAction,
        params: {
          queryParams: {
            [typeAgg]: workAgg,
            [profileAgg]: profile?.id,
            [queryAgg]: value,
          },
        },
      }),
    );
  }, 500);

  const getProductionPrivacyAttributeId = async production => {
    const productionPrivacyData = await API.getOne(PRODUCTIONS, `${production.id}/privacy`);
    return productionPrivacyData?.data?.data.find(el => el.profile?.id === profile.id)?.id;
  };

  const updateProductionPrivacy = async (production, body) => {
    const isRemove = Object.keys(body).includes('isRemoved');
    const privacyAttributeId = await getProductionPrivacyAttributeId(production);
    if (privacyAttributeId) {
      dispatch(
        updateOne({
          endpoint: PRODUCTION_PRIVACY(production.id),
          sagaRoutine: isRemove ? removeProductionAction : null,
          id: privacyAttributeId,
          params: body,
          productionId: production.id,
        }),
      );
    } else {
      dispatch(
        createEntity({
          endpoint: PRODUCTION_PRIVACY(production.id),
          sagaRoutine: isRemove ? removeProductionAction : null,
          params: body,
          productionId: production.id,
        }),
      );
    }
  };

  const setProductionVisibility = async (value, production) => {
    const body = {
      profile: {
        id: profile?.id,
      },
      privacyAttribute: {
        id: value,
      },
    };
    await updateProductionPrivacy(production, body);
  };

  const editProductionLinkProps = (production, step) => ({
    pathname: `${getRouteWithSlug(route.ARTISTS, profile)}/edit/modify-production`,
    query: { production_id: production.id, step },
  });

  const publishProduction = () => {
    if (interactedProduction) {
      dispatch(
        createEntity({
          endpoint: `${PRODUCTIONS}/${interactedProduction.id}/actions`,
          sagaRoutine: publishProductionAction,
          params: {
            type: 'publish',
            addedBy: { id: PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST },
            privacyAttribute: {
              id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
            },
            profile: {
              id: profile?.id,
            },
          },
          callback: () => {
            seasonChange(FilterFormRef.current?.values[seasonAgg]);
          },
        }),
      );
    }
    setInteractedProduction(null);
  };

  const deleteProduction = () => {
    if (deletedProduction) {
      dispatch(
        deleteEntity({
          endpoint: PRODUCTIONS,
          sagaRoutine: deleteProductionAction,
          id: deletedProduction.id,
        }),
      );
    }
    setDeletedProduction(null);
  };

  const removeProduction = async () => {
    const body = {
      profile: {
        id: profile.id,
      },
      isRemoved: true,
    };
    await updateProductionPrivacy(removedProduction, body);
    setRemovedProduction(null);
  };

  const canDeleteProduction = production => {
    const hasPermissionsToDeleteProduction = canDoProductionAction(
      productionPermissions?.canDelete,
      PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST,
      profile?.id,
      production,
    );
    if (production?.published_at) {
      return (
        productionPermissions?.canDeleteAsAdmin ||
        (hasPermissionsToDeleteProduction &&
          checkDateForProductionDeletion(
            get(userConstants, '[0].MINIMUM_PRODUCTION_DELETION_DATE'),
            production?.published_at,
          ))
      );
    }
    return productionPermissions?.canDeleteAsAdmin || hasPermissionsToDeleteProduction;
  };

  return (
    <>
      <Legend title={<H3>{t(get(tab, 'name'))}</H3>} />
      <Title>{t(get(tab, 'subtitle'))}</Title>
      <div>
        <section>
          <div className={classes.actionBlock}>
            <Formik initialValues={{ [workAgg]: null, [seasonAgg]: productionsType }} innerRef={FilterFormRef}>
              {() => (
                <Form className={classes.filterForm}>
                  <Grid container spacing={1} justify="flex-start">
                    <Grid item sm={3} xs={12}>
                      <Field
                        instanceId="season_date"
                        key="season_date"
                        name={seasonAgg}
                        onChange={seasonChange}
                        options={getSeasonsOptions()}
                        label={t(`${TP}.m_SELECT2`)}
                        component={FormikAutocompleteSelect}
                      />
                    </Grid>
                    <Grid item sm={9} xs={12}>
                      <Field
                        instanceId="musical_work_search"
                        label={t(`${TP}.m_MUSICALWORK`)}
                        name={workAgg}
                        placeholder={t(`${TP}.FN_SEARCH_BY_MUSICAL_WORK`)}
                        onChange={setMusicalWorkValue}
                        onInputTextChange={getWorksList}
                        options={works?.options || []}
                        isLoading={works?.loading}
                        minSearchCharsLength={0}
                        resetOptions={clearMusicalWorksList}
                        component={FormikAutocompleteSelect}
                        isClearable
                        isEditable
                        customComponents={{
                          DropdownIndicator: () => <SearchIcon />,
                        }}
                        onFocus={() => dispatch(setStartingOfMVAggregation())}
                        startAggregations={works?.startSearching}
                      />
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
            <Button onClick={goToAddNewProductionPage} className={classes.addButton}>
              {t(`${TP}.m_ADDSCHEDULE`)}
            </Button>
          </div>
        </section>
        <Divider classes={{ root: classes.divider }} />
        <section>
          {artistProductions?.data?.length === 0 && !artistProductions?.loading && t(`${TP}.NO_RESULTS`)}
          {artistProductions?.loading ? (
            <FbCircularProgress />
          ) : (
            <>
              <Grid container spacing={4} direction="column">
                {(artistProductions?.data || []).map(production => (
                  <Grid item key={production?.id} className={classes.cardWrapper}>
                    <EditArtistProductionCard
                      production={production}
                      profile={profile}
                      setVisibility={setProductionVisibility}
                      privacyOptions={privacyOptions}
                      canEdit={isAdmin || canArtistEditProduction(production, profile?.id)}
                      onPublish={
                        (isAdmin ||
                          canDoProductionAction(
                            productionPermissions?.canPublish,
                            PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST,
                            profile?.id,
                            production,
                          )) &&
                        !production?.published_at
                          ? () => setInteractedProduction(production)
                          : null
                      }
                      deleteProduction={
                        isAdmin || canDeleteProduction(production) ? () => setDeletedProduction(production) : null
                      }
                      editProductionLinkProps={editProductionLinkProps}
                      removeProduction={production?.published_at ? () => setRemovedProduction(production) : null}
                      isLoading={productionLoading === production.id}
                    />
                  </Grid>
                ))}
              </Grid>
              <FbPagination
                filters={artistProductions}
                fetchFunction={paginationPageChange}
                onPageChange={() =>
                  scroller.scrollTo('schedule', { offset: mobileView ? -180 : -150, smooth: true, duration: 250 })
                }
              />
            </>
          )}
        </section>
        {interactedProduction?.id && (
          <PublishProductionModal
            production={interactedProduction}
            closeModal={() => setInteractedProduction(null)}
            publish={publishProduction}
            withCheckmark
          />
        )}
        {removedProduction?.id && (
          <ConfirmDeleteModal
            closeModal={() => setRemovedProduction(null)}
            onDelete={removeProduction}
            title={t(`${TP}.FN_DELETE_PRODUCTION_MODAL_TITLE`)}
            text={<ParseHtml>{t(`${TP}.FN_DELETE_PRODUCTION_MODAL_TEXT`)}</ParseHtml>}
            actionButtonLabel={`${TP}.FN_REMOVE`}
          />
        )}
        {deletedProduction?.id && (
          <ConfirmDeleteModal
            closeModal={() => setDeletedProduction(null)}
            onDelete={deleteProduction}
            title={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_TITLE`, { name: deletedProduction?.name })}
            text={t(`${TP}.FN_EDITSEASON_APPROVE_DELETE_PRODUCTION_INFO`)}
          />
        )}
      </div>
    </>
  );
}

PerformancesTab.propTypes = {
  t: PropTypes.func.isRequired,
  tab: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  privacyOptions: PropTypes.array,
};

export default memo(PerformancesTab);
